/**
 * Aquanara Beauty — Animations Script
 * Powered by GSAP, ScrollTrigger, and Lenis smooth scroll.
 * Apple-style canvas/3D feel scroll effects, parallax and split reveals.
 */

document.addEventListener('DOMContentLoaded', () => {
  // Initialize Smooth Scrolling (Lenis)
  let lenis;
  if (typeof Lenis !== 'undefined') {
    lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      direction: 'vertical',
      gestureDirection: 'vertical',
      smooth: true,
      mouseMultiplier: 1,
      smoothTouch: false,
      touchMultiplier: 2,
      infinite: false,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    // Integrate Lenis with GSAP ScrollTrigger
    if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
      lenis.on('scroll', ScrollTrigger.update);
      gsap.ticker.add((time) => {
        lenis.raf(time * 1000);
      });
      gsap.ticker.lagSmoothing(0);
    }
  }

  // GSAP Animations
  if (typeof gsap !== 'undefined') {
    gsap.registerPlugin(ScrollTrigger);

    initHeroAnimations();
    initProductHeroPin();
    initRevealAnimations();
    initFlavorInteractive();
  }
});

// 1. Hero Reveal (Page load)
function initHeroAnimations() {
  const tl = gsap.timeline();
  
  tl.to('.hero-headline', {
    y: 0,
    opacity: 1,
    duration: 1.2,
    ease: 'power4.out',
    delay: 0.3
  })
  .to('.hero-sub', {
    y: 0,
    opacity: 1,
    duration: 1.0,
    ease: 'power3.out'
  }, '-=0.8')
  .to('.hero-ctas', {
    y: 0,
    opacity: 1,
    duration: 0.8,
    ease: 'power2.out'
  }, '-=0.6')
  .from('.scroll-hint', {
    opacity: 0,
    duration: 0.6
  }, '-=0.2');
}

// 2. Product Hero Pin (Apple-style scroll showcase)
function initProductHeroPin() {
  const section = document.querySelector('.product-hero-sec');
  const lifestyleImg = document.querySelector('.product-hero-lifestyle-img');
  const rightPanel = document.querySelector('.product-hero-right-panel');

  if (!section) return;

  // Parallax effect on the lifestyle image on scroll
  if (lifestyleImg) {
    gsap.fromTo(lifestyleImg,
      { yPercent: -8 },
      {
        yPercent: 8,
        ease: 'none',
        scrollTrigger: {
          trigger: section,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true
        }
      }
    );
  }

  // Smooth entrance for the elements in the right panel on viewport entry
  if (rightPanel) {
    const showcase = document.querySelector('.product-image-showcase');
    const details = document.querySelector('.product-details-content');
    
    if (showcase && details) {
      gsap.fromTo(showcase,
        { x: -30, opacity: 0 },
        {
          x: 0,
          opacity: 1,
          duration: 0.8,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: section,
            start: 'top 70%',
            toggleActions: 'play none none none'
          }
        }
      );
      
      gsap.fromTo(details.children,
        { y: 25, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          stagger: 0.12,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: section,
            start: 'top 70%',
            toggleActions: 'play none none none'
          }
        }
      );
    }
  }
}

// 3. Reveal elements when entering viewport
function initRevealAnimations() {
  // Parallax elements
  const parallaxMedia = document.querySelectorAll('.bundle-media, .dayanara-img-wrap');
  parallaxMedia.forEach(wrap => {
    const img = wrap.querySelector('img');
    if (!img) return;

    gsap.fromTo(img, 
      { yPercent: -10 },
      {
        yPercent: 10,
        ease: 'none',
        scrollTrigger: {
          trigger: wrap,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true
        }
      }
    );
  });

  // Reveal sections with fade-in-up
  const revealSections = document.querySelectorAll('.bundle-info, .dayanara-info, .mask-info, .ritual-step, .lifestyle-arch-container');
  revealSections.forEach(el => {
    gsap.fromTo(el,
      { y: 50, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 1.0,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: el,
          start: 'top 85%',
          toggleActions: 'play none none none'
        }
      }
    );
  });

  // Dayanara story quote line-by-line reveal
  const quote = document.querySelector('.dayanara-quote');
  if (quote) {
    gsap.fromTo(quote,
      { opacity: 0, y: 30 },
      {
        opacity: 1,
        y: 0,
        duration: 1.2,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: quote,
          start: 'top 80%'
        }
      }
    );
  }
}

// 4. Flavor interactive swatches & background transitions
function initFlavorInteractive() {
  const swatches = document.querySelectorAll('.swatch');
  const section = document.querySelector('.product-hero-sec');
  const productImg = document.querySelector('.active-product-img');
  const flavorTitle = document.querySelector('.active-flavor-title');
  const flavorDesc = document.querySelector('.active-flavor-description');
  const buyBtn = document.querySelector('.product-buy-btn');

  if (!swatches.length || !section || !productImg) return;

  const flavorsInfo = {
    strawberry: {
      bg: '#FFF0F3',
      name: { es: 'Fresa', en: 'Strawberry' },
      desc: {
        es: 'Agua de colágeno marino con sabor natural a fresa. Sin azúcar añadida y con solo 20 calorías.',
        en: 'Marine collagen water with natural strawberry flavor. No added sugar and only 20 calories.'
      },
      img: '🍓',
      file: window.ShopifyThemeAssets?.fresaMain || 'shopify-theme/assets/aquanara_00015_transparent_fixed.png?v=6.0',
      canFile: window.ShopifyThemeAssets?.fresaCan || 'shopify-theme/assets/aquanara_00029_transparent.png?v=6.0',
      color: '#E4007C',
      price: '29.90',
      id: 'collagen-fresa'
    },
    pineapple: {
      bg: '#FFFDF0',
      name: { es: 'Piña', en: 'Pineapple' },
      desc: {
        es: 'Un estallido jugoso de piña con colágeno marino. Una bebida refrescante sin azúcar añadida y solo 20 calorías.',
        en: 'A juicy burst of pineapple with marine collagen. A refreshing beverage with no added sugar and only 20 calories.'
      },
      img: '🍍',
      file: window.ShopifyThemeAssets?.pinaMain || 'shopify-theme/assets/aquanara_00013_transparent_fixed.png?v=6.0',
      canFile: window.ShopifyThemeAssets?.pinaCan || 'shopify-theme/assets/aquanara_00027_transparent.png?v=6.0',
      color: '#C8973A',
      price: '29.90',
      id: 'collagen-pina'
    },
    lime: {
      bg: '#F0FFF4',
      name: { es: 'Lima', en: 'Lime' },
      desc: {
        es: 'Acidez cítrica y refrescante con colágeno marino. Con solo 20 calorías y sin azúcar añadida, perfecta para disfrutar a diario.',
        en: 'Refreshing citrus acidity with marine collagen. With only 20 calories and no added sugar, perfect for daily enjoyment.'
      },
      img: '🍋',
      file: window.ShopifyThemeAssets?.limaMain || 'shopify-theme/assets/aquanara_00014_transparent_fixed.png?v=6.0',
      canFile: window.ShopifyThemeAssets?.limaCan || 'shopify-theme/assets/aquanara_00028_transparent.png?v=6.0',
      color: '#2E7D32',
      price: '29.90',
      id: 'collagen-lima'
    }
  };

  swatches.forEach(sw => {
    sw.addEventListener('click', () => {
      // Remove active from other swatches
      swatches.forEach(s => s.classList.remove('active'));
      sw.classList.add('active');

      const flavor = sw.getAttribute('data-flavor');
      const data = flavorsInfo[flavor];

      if (data) {
        // Transition section background color
        gsap.to(section, {
          backgroundColor: data.bg,
          duration: 0.8,
          ease: 'power2.out'
        });

        // Update soft color aura behind product image
        const showcase = document.querySelector('.product-image-showcase');
        if (showcase) {
          const glowColors = {
            strawberry: 'radial-gradient(circle, rgba(228, 0, 124, 0.09) 0%, rgba(228, 0, 124, 0) 72%)',
            pineapple: 'radial-gradient(circle, rgba(200, 151, 58, 0.09) 0%, rgba(200, 151, 58, 0) 72%)',
            lime: 'radial-gradient(circle, rgba(46, 125, 50, 0.09) 0%, rgba(46, 125, 50, 0) 72%)'
          };
          showcase.style.setProperty('--flavor-aura', glowColors[flavor]);
        }

        // 1. Smooth elegant slide-and-fade transition for the product packshot
        gsap.timeline()
          .to(productImg, {
            y: 15,
            opacity: 0,
            duration: 0.22,
            ease: 'power2.in'
          })
          .add(() => {
            productImg.setAttribute('src', data.file);
          })
          .to(productImg, {
            y: 0,
            opacity: 1,
            duration: 0.4,
            ease: 'power2.out'
          });

        const currentLang = (document.documentElement.lang || 'es').substring(0, 2).toLowerCase();

        // 2. Transition Flavor Title
        if (flavorTitle) {
          gsap.timeline()
            .to(flavorTitle, { opacity: 0, y: -10, duration: 0.15 })
            .add(() => {
              flavorTitle.textContent = data.name[currentLang] || data.name['es'];
              flavorTitle.style.color = data.color;
            })
            .to(flavorTitle, { opacity: 1, y: 0, duration: 0.25 });
        }

        // 3. Transition Description
        if (flavorDesc) {
          gsap.timeline()
            .to(flavorDesc, { opacity: 0, duration: 0.15 })
            .add(() => {
              flavorDesc.textContent = data.desc[currentLang] || data.desc['es'];
            })
            .to(flavorDesc, { opacity: 0.85, duration: 0.25 });
        }

        // 4. Update Buy Button data attributes and text
        if (buyBtn) {
          buyBtn.setAttribute('data-id', data.id);
          buyBtn.setAttribute('data-title', `Aquanara ${data.name[currentLang]}`);
          buyBtn.setAttribute('data-flavor', data.name[currentLang]);
          buyBtn.setAttribute('data-img', data.img);
          
          const btnText = currentLang === 'es' 
            ? `Comprar 12-Pack — $${data.price}` 
            : `Buy 12-Pack — $${data.price}`;
          buyBtn.innerHTML = btnText;
        }
      }
    });

    // Hover transitions (subtle section color shifting)
    sw.addEventListener('mouseenter', () => {
      const flavor = sw.getAttribute('data-flavor');
      const data = flavorsInfo[flavor];
      if (data && !sw.classList.contains('active')) {
        gsap.to(section, {
          backgroundColor: data.bg,
          duration: 0.4,
          ease: 'power1.out'
        });
      }
    });

    sw.addEventListener('mouseleave', () => {
      // Revert to active swatch bg
      const activeSw = document.querySelector('.swatch.active');
      if (activeSw) {
        const flavor = activeSw.getAttribute('data-flavor');
        const data = flavorsInfo[flavor];
        if (data) {
          gsap.to(section, {
            backgroundColor: data.bg,
            duration: 0.4,
            ease: 'power1.out'
          });
        }
      }
    });
  });

  // Hover effect on the product showcase: swaps 12-pack image for single can
  const showcase = document.querySelector('.product-image-showcase');
  if (showcase && productImg) {
    showcase.addEventListener('mouseenter', () => {
      const activeSw = document.querySelector('.swatch.active');
      if (activeSw) {
        const flavor = activeSw.getAttribute('data-flavor');
        const data = flavorsInfo[flavor];
        if (data && data.canFile) {
          gsap.killTweensOf(productImg);
          gsap.timeline()
            .to(productImg, { y: 12, opacity: 0, duration: 0.16, ease: 'power2.in' })
            .add(() => {
              productImg.setAttribute('src', data.canFile);
            })
            .to(productImg, { y: 0, opacity: 1, duration: 0.22, ease: 'power2.out' });
        }
      }
    });

    showcase.addEventListener('mouseleave', () => {
      const activeSw = document.querySelector('.swatch.active');
      if (activeSw) {
        const flavor = activeSw.getAttribute('data-flavor');
        const data = flavorsInfo[flavor];
        if (data && data.file) {
          gsap.killTweensOf(productImg);
          gsap.timeline()
            .to(productImg, { y: 12, opacity: 0, duration: 0.16, ease: 'power2.in' })
            .add(() => {
              productImg.setAttribute('src', data.file);
            })
            .to(productImg, { y: 0, opacity: 1, duration: 0.22, ease: 'power2.out' });
        }
      }
    });
  }

  // Relisten to language changes to update active labels instantly and silently
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes' && mutation.attributeName === 'lang') {
        const activeSw = document.querySelector('.swatch.active');
        if (activeSw) {
          const flavor = activeSw.getAttribute('data-flavor');
          const data = flavorsInfo[flavor];
          const currentLang = (document.documentElement.lang || 'es').substring(0, 2).toLowerCase();
          
          if (flavorTitle && data) {
            flavorTitle.textContent = data.name[currentLang] || data.name['es'];
          }
          if (flavorDesc && data) {
            flavorDesc.textContent = data.desc[currentLang] || data.desc['es'];
          }
          if (buyBtn && data) {
            buyBtn.setAttribute('data-title', `Aquanara ${data.name[currentLang]}`);
            buyBtn.setAttribute('data-flavor', data.name[currentLang]);
            const btnText = currentLang === 'es' 
              ? `Comprar 12-Pack — $${data.price}` 
              : `Buy 12-Pack — $${data.price}`;
            buyBtn.innerHTML = btnText;
          }
        }
      }
    });
  });
  observer.observe(document.documentElement, { attributes: true });

  // Run once on page load to set flavor detail language correctly
  const activeSw = document.querySelector('.swatch.active');
  if (activeSw) {
    const flavor = activeSw.getAttribute('data-flavor');
    const data = flavorsInfo[flavor];
    const currentLang = (document.documentElement.lang || 'es').substring(0, 2).toLowerCase();
    if (data) {
      if (flavorTitle) {
        flavorTitle.textContent = data.name[currentLang] || data.name['es'];
        flavorTitle.style.color = data.color;
      }
      if (flavorDesc) {
        flavorDesc.textContent = data.desc[currentLang] || data.desc['es'];
      }
      if (buyBtn) {
        buyBtn.setAttribute('data-id', data.id);
        buyBtn.setAttribute('data-flavor', data.name[currentLang]);
        buyBtn.setAttribute('data-title', `Aquanara ${data.name[currentLang]}`);
        buyBtn.setAttribute('data-price', data.price);
        const btnText = currentLang === 'es' 
          ? `Comprar 12-Pack — $${data.price}` 
          : `Buy 12-Pack — $${data.price}`;
        buyBtn.innerHTML = btnText;
      }
    }
  }
}
